import JSZip from 'jszip';
import storageService from './storage.service.js';

var objectURLs = {};

var createObjectURL = (type, blob) => {
  const url = URL.createObjectURL(blob);
  if (!objectURLs[type]) {
    objectURLs[type] = [];
  }
  objectURLs[type].push(url);
  return url;
}

var cleanObjectURLs = function(type) {
  if (type) {
    if (objectURLs[type]) {
      objectURLs[type].forEach(URL.revokeObjectURL);
    }
    delete objectURLs[type];
  } else {
    for (const urls of Object.values(objectURLs)) {
      urls.forEach(URL.revokeObjectURL);
    }
    objectURLs = {};
  }
}

var readFile = (file) => new Promise((resolve, reject) => {
  cleanObjectURLs(null);
  let reader = new FileReader();
  reader.onload = () => {
    resolve(reader.result);
  };
  reader.onerror = () => {
    reject(reader.error);
  };
  reader.readAsArrayBuffer(file);
});

var loadImage = (type, blob) => new Promise((resolve, reject) => {
  const img = new Image(),
    url = createObjectURL(type, blob);
  img.onload = () => {
    resolve({
      img,
      url,
      width: img.width,
      height: img.height,
    });
  };
  img.onerror = (msg) => {
    reject(msg);
  };
  img.src = url;
});

var designService = {
  loadedFile: null,
  currentDesign: null,
  blockImages: null,
  tileImages: null,
  billOfMaterials: null,
  emptySubmeshes: [],
  savedMetadata: {},
  zipFile: null,
  currentBlock: null,

  async readFile(file) {
    this.currentDesign = null;
    this.currentBlock = null;
    this.loadedFile = file;
    let contents = await readFile(file);
    let zip = await (new JSZip().loadAsync(contents));
    let design = JSON.parse(await zip.file('design.json').async('string'));
    
    if (design.version !== 2) {
      throw 'VersionError';
    }

    this.blockImages = [];
    for (let i = 0; i < design.blockCount; i++) {
      let imgBlob = await zip.file(`blocks/${i}/image.jpg`).async('blob');
      let img = await loadImage('block', imgBlob);
      this.blockImages[i] = img;
    }

    this.tileImages = [];
    for (const [tileRef, tile] of Object.entries(design.tileRefs)) {
      let imgBlob = await zip.file(`tiles/${tileRef}.png`).async('blob'),
              plainBlob = await zip.file(`tiles/plain/${tileRef}.png`).async('blob');
      let img = await loadImage('tile', imgBlob),
              plain = await loadImage('tile', plainBlob);
      this.tileImages[tileRef] = {
        img,
        plain,
        tile
      };
    }

    if (design.billOfMaterials) {
      this.billOfMaterials = [];
      for (const [tileRef, count] of Object.entries(design.billOfMaterials)) {
        this.billOfMaterials.push({
          tileRef: parseInt(tileRef),
          count
        });
      }
      this.billOfMaterials.sort((a, b) => b.count - a.count);
    }
    
    this.savedMetadata = await storageService.loadFileMetadata(file);
    if (!this.savedMetadata.finishedSubmeshes) {
      this.savedMetadata.finishedSubmeshes = {};
    }

    this.currentDesign = design;
    this.zipFile = zip;
    return this.currentDesign;
  },

  isValid() {
    return !!this.currentDesign;
  },

  getTileImage(ref) {
    return this.tileImages[ref];
  },

  getCurrentDesign() {
    return this.currentDesign;
  },

  getBillOfMaterials() {
    return this.billOfMaterials;
  },
  
  isSubmeshFinished(idx) {
    const finished = this.savedMetadata.finishedSubmeshes[idx];
    return typeof finished !== 'undefined' && finished;
  },
  
  markSubmeshFinished(idx, finished) {
    this.savedMetadata.finishedSubmeshes[idx] = finished;
    storageService.saveFileMetadata(this.loadedFile, this.savedMetadata);
  },
  
  getFinishedSubmeshes() {
    return JSON.parse(JSON.stringify(this.savedMetadata.finishedSubmeshes));
  },

  getBlockImages() {
    return this.blockImages;
  },

  async loadBlock(blockIdx) {
    blockIdx = parseInt(blockIdx);
    if (this.currentBlock && this.currentBlock.blockIdx === blockIdx) {
      return Promise.resolve(this.currentBlock);
    }
    if (blockIdx >= this.currentDesign.blockCount) {
      return null;
    }
    
    cleanObjectURLs('submesh');

    const block = JSON.parse(await this.zipFile.file(`blocks/${blockIdx}/block.json`).async('string'));

    block.blockIdx = blockIdx;

    const submeshImages = [];
    const emptySubmeshes = [];

    const submeshCount = this.currentDesign.metadata.hsubmeshes * this.currentDesign.metadata.vsubmeshes,
            maxSubmeshX = Math.min(block.startSubmeshX + block.sizeX, this.currentDesign.metadata.hsubmeshes),
            maxSubmeshY = Math.min(block.startSubmeshY + block.sizeY, this.currentDesign.metadata.vsubmeshes);
    let i = 0;
    outer: for (let y = block.startSubmeshY; y < maxSubmeshY; y++) {
      for (let x = block.startSubmeshX; x < maxSubmeshX; x++) {
        const submeshIdx = y * this.currentDesign.metadata.hsubmeshes + x;
        if (submeshIdx >= submeshCount) {
          break outer;
        }
        let imgBlob = await this.zipFile.file(`blocks/${blockIdx}/submeshes/${submeshIdx}.jpg`).async('blob');
        let img = await loadImage('submesh', imgBlob);
        submeshImages[i] = img;
        if (!block.submeshes[i] || !block.submeshes[i].tiles || !block.submeshes[i].tiles.length) {
          emptySubmeshes.push(submeshIdx);
        }
        i++;
      }
    }

    block.submeshImages = submeshImages;
    block.emptySubmeshes = emptySubmeshes;

    this.currentBlock = block;

    return block;
  },

  getCurrentBlock() {
    return this.currentBlock;
  }
  
};

export default designService;
